import type { ReactElement } from "react"
import { useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useDispatch } from "react-redux"
import { Link, useNavigate } from "react-router-dom"

import * as Yup from "yup"
import { Grid, useMediaQuery } from "@mui/material"
import type { CredentialResponse } from "@react-oauth/google"
import { GoogleLogin } from "@react-oauth/google"
import { Button, InputPassword, InputText, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { Form, FormikProvider, useFormik, useFormikContext } from "formik"
import { t } from "i18next"
import { type JwtPayload, jwtDecode } from "jwt-decode"

import { Divider } from "../../../components/core/Divider"
import { SidAPI } from "../../../services/SidAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { CustomTokenObtainPair } from "../../../types/generated/authentication/CustomTokenObtainPair"
import type { TokenObtainPairResponse } from "../../../types/generated/authentication/TokenObtainPairResponse"
import { getEnvVar, isEnvVariableTruthy } from "../../../utils/genericHelpers"
import { setAuthTokens } from "../Slice"
import type { LoginContainerProps, LoginFormikValues, LoginProps } from "../interfaces"
import { EntryDisclaimer } from "./EntryDisclaimer"
import { VerificationContainer as Verification } from "./VerificationScreen"

import styles from "./Login.module.scss"

/**
 * Login component that renders the login form and handles authentication UI states
 *
 * @param {boolean} isLoginSuccess - Indicates if login attempt was successful
 * @param {AxiosError} loginError - Error details if login failed
 * @param {boolean} isResendVerificationSuccess - Indicates if verification email was resent successfully
 * @param {boolean} isGoogleLoginFailure - Indicates if Google login failed
 * @param {boolean} isGoogleAccVerified - Indicates if Google account is verified
 * @param {(response: CredentialResponse) => Promise<void>} onGoogleLoginSuccess - Callback for successful Google login
 * @returns {ReactElement} Login form with authentication states
 */
export function Login(props: Readonly<LoginProps>): ReactElement {
  const {
    isLoginSuccess,
    loginError,
    isResendVerificationSuccess,
    isGoogleLoginFailure,
    isGoogleAccVerified,
    onGoogleLoginSuccess,
  } = props

  const { t, i18n } = useTranslation("auth")

  // Get form state and handlers from Formik context
  const { values, errors, touched, isSubmitting, isValid, status, handleChange, handleBlur, submitForm } =
    useFormikContext<LoginFormikValues>()

  const navigate = useNavigate()
  // Responsive breakpoint for Google login button width
  const breakpointMatch = useMediaQuery("(max-width:400px)")

  // Check if Google Sign-in is enabled in environment config
  const isGoogleSigninEnabled = isEnvVariableTruthy(String(getEnvVar("KONAN_SSO_GOOGLE_ENABLED")))

  // Local state for Google login error
  const [isGoogleLoginError, setIsGoogleLoginError] = useState<boolean>(false)

  return (
    <Grid item container className={styles.mainContainer}>
      {/* success message. if user logs in && isn't verified and resend a verification email to themselves. */}
      {isLoginSuccess ||
        (isResendVerificationSuccess && (
          <Snackbar fullWidth description={t("login.verificationSent")} severity={"success"} />
        ))}

      {/* Network error; shown if client cant reach api for any reason, or failed login prompt */}
      {status && status?.error && loginError?.response?.status !== 403 && (
        <Snackbar
          fullWidth
          severity={"error"}
          description={
            loginError?.message === "Network Error" ? t("login.networkError") : t("login.invalidCredentials")
          }
        />
      )}

      {/**
       * Google Login errors
       * 1. google login error
       * 2. google account isn't verified
       * 3. google signup failure for an unregistered account trying to login using google
       */}
      {isGoogleLoginError && <Snackbar fullWidth severity="error" description={t("login.googleError")} />}

      {!isGoogleAccVerified && (
        <Snackbar fullWidth severity="error" description={t("login.googleAccountNotVerified")} />
      )}

      {isGoogleLoginFailure && <Snackbar fullWidth severity="error" description={t("login.googleLoginFailure")} />}

      <Form>
        <Grid container className={styles.formContainer}>
          <Grid item container xs={12} justifyContent="start" gap={1.5}>
            <Typography variant="h1-bold">{t("login.title")}</Typography>

            <Typography variant="a">
              {t("login.subtitle")}{" "}
              {/* TODO:: replace with SUI anchor component in the revamp leaving the styles inline to remember to remove them when revamping */}
              <Link id="signup-link" to="/register" className={styles.link}>
                {t("login.signUpLink")}
              </Link>
            </Typography>
          </Grid>

          {/* Login form inputs */}
          <InputText
            fullWidth
            required
            hideDescription={!Boolean(Boolean(errors.email) && touched.email)}
            id="email"
            label={t("login.email.label")}
            placeholder={t("login.email.placeholder")}
            value={values.email}
            error={Boolean(errors.email) && touched.email && errors.email}
            handleChange={handleChange}
            handleBlur={handleBlur}
            disabled={isSubmitting}
          />

          <InputPassword
            fullWidth
            id="password"
            handleHelperClick={() => navigate("/reset-password")}
            value={values.password}
            error={Boolean(errors.password) && touched.password && errors.password}
            handleChange={handleChange}
            handleBlur={handleBlur}
            disabled={isSubmitting}
          />

          {/* Submit button - Disabled when form is invalid or submitting */}
          <Button
            fullWidth
            id="login-button"
            variant="primary"
            type="submit"
            onClick={() => submitForm()}
            disabled={isSubmitting || !isValid}
            isLoading={isSubmitting}
          >
            {t("login.submit")}
          </Button>

          {/* Google Sign-in section - Only shown if enabled in config */}
          {isGoogleSigninEnabled && (
            <Grid item container justifyContent="center" gap={3}>
              <Divider text={t("login.or")} mt={1.25} mb={1.25} />

              <GoogleLogin
                width={breakpointMatch ? 252 : 312}
                theme="outline"
                size="medium"
                text="signin_with"
                onSuccess={onGoogleLoginSuccess}
                onError={() => {
                  setIsGoogleLoginError(true)
                }}
                auto_select={false}
                locale={i18n.language}
              />
            </Grid>
          )}
        </Grid>
      </Form>

      <EntryDisclaimer type="signin" />
    </Grid>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const validationSchema = Yup.object({
  email: Yup.string()
    .email(t("login.email.invalid", { ns: "auth" }))
    .trim()
    .required(t("login.email.required", { ns: "auth" })),
  password: Yup.string().required(t("login.password.required", { ns: "auth" })),
})

/**
 * Container component that manages the login state and form submission logic.
 * Handles both traditional and Google OAuth authentication flows.
 *
 * @param {string} redirectPath - Path to redirect to after successful login
 * @returns {ReactElement} The login form
 */
export function LoginContainer(props: Readonly<LoginContainerProps>): ReactElement {
  const { redirectPath = "/projects?page=model" } = props

  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Track Google authentication states
  const [isGoogleAccVerified, setIsGoogleAccVerified] = useState<boolean>(true)
  const [isGoogleLoginFailure, setIsGoogleLoginFailure] = useState<boolean>(false)

  // Set up API mutation hooks for authentication flows
  const loginMutation = useMutation<AxiosResponse<TokenObtainPairResponse>, AxiosError, CustomTokenObtainPair>(
    SidAPI.login,
  )
  const resendVerificationMutation = useMutation<AxiosResponse<void>, AxiosError, string>(SidAPI.resendVerificationLink)

  const googleLoginMutation = useMutation<AxiosResponse<TokenObtainPairResponse>, AxiosError, string>(
    SidAPI.googleLogin,
  )

  // Initialize Formik form with validation and submission handler
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async ({ email, password }, { setStatus }) => {
      await queryClient.invalidateQueries()

      try {
        // Attempt login and store tokens on success
        const response = await loginMutation.mutateAsync({ email, password })

        dispatch(setAuthTokens({ access: response.data.access, refresh: response.data.refresh }))
        // navigate based on the url the user tried to access with
        navigate(redirectPath, { replace: true })
      } catch (e) {
        setStatus({
          error: t("login.error", { ns: "auth" }),
        })
      }
    },
  })

  // Handle Google OAuth authentication flow
  const onGoogleLoginSuccess = async (credentialResponse: CredentialResponse): Promise<void> => {
    await queryClient.invalidateQueries()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let response: any

    // Checks if google account is verified before login/SignUp
    // if account isn't verified login isn't attempted

    const decodedToken = jwtDecode<JwtPayload & { email_verified?: boolean }>(credentialResponse.credential as string)
    if (decodedToken.email_verified === false) {
      setIsGoogleAccVerified(false)
      return
    }

    try {
      // First attempt to login with Google
      response = await googleLoginMutation.mutateAsync(credentialResponse.credential as string)
    } catch (error) {
      // If login fails, attempt to sign up the user
      try {
        response = await SidAPI.googleSignup(credentialResponse.credential as string)
      } catch (error) {
        setIsGoogleLoginFailure(true)
        return
      }
    }

    // Store authentication tokens and redirect on success
    const data = response.data
    dispatch(setAuthTokens({ access: data.access, refresh: data.refresh }))
    navigate(redirectPath, { replace: true })
  }

  // If user isn't verified and logs in, gets navigated to Verification screen else login screen
  if (loginMutation?.error?.response?.status === 403 || googleLoginMutation.error?.response?.status === 403)
    return <Verification email={formik.values.email} />

  return (
    <FormikProvider value={formik}>
      <Login
        isLoginSuccess={loginMutation.isSuccess}
        loginError={loginMutation.error}
        isResendVerificationSuccess={resendVerificationMutation.isSuccess}
        isGoogleLoginFailure={isGoogleLoginFailure}
        isGoogleAccVerified={isGoogleAccVerified}
        onGoogleLoginSuccess={onGoogleLoginSuccess}
      />
    </FormikProvider>
  )
}
